import { useEffect, useRef, useState } from "react";
import {
  CalendarClock,
  Check,
  CloudUpload,
  Copy,
  FileArchive,
  Loader2,
  LockKeyhole,
  LogOut,
} from "lucide-react";
import { toast } from "sonner";
import api, { DOWNLOAD_URL, formatApiErrorDetail } from "../lib/api";
import { LogoMark } from "../components/Logo";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const InfoTile = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-line bg-paper px-4 py-3.5">
    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-ink/40">
      {label}
    </p>
    <p className="mt-1 truncate text-sm font-semibold text-ink" data-testid={testid}>
      {value}
    </p>
  </div>
);

const LoginCard = ({ onLoggedIn }) => {
  const [email, setEmail] = useState("ops@dentassist.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      toast.success("Welcome back.");
      onLoggedIn(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div className="w-full max-w-md rounded-3xl border border-line bg-white p-8 shadow-soft sm:p-10">
      <LogoMark className="h-12 w-12" />
      <h1 className="mt-5 font-display text-3xl font-medium tracking-tight">Installer manager</h1>
      <p className="mt-2 text-sm leading-relaxed text-ink/55">
        Sign in with the operations account to upload or replace AssisDent.msi.
      </p>
      <form onSubmit={submit} className="mt-7 space-y-4">
        <div>
          <label htmlFor="admin-email" className="text-xs font-semibold uppercase tracking-wide text-ink/50">
            Email
          </label>
          <input
            id="admin-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-email-input"
            className="mt-1.5 w-full rounded-xl border border-line bg-paper px-4 py-3 text-sm font-medium outline-none transition-[border-color,box-shadow] focus:border-brand focus:ring-2 focus:ring-brand/20"
          />
        </div>
        <div>
          <label htmlFor="admin-password" className="text-xs font-semibold uppercase tracking-wide text-ink/50">
            Password
          </label>
          <input
            id="admin-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            placeholder="Your admin password"
            className="mt-1.5 w-full rounded-xl border border-line bg-paper px-4 py-3 text-sm font-medium outline-none transition-[border-color,box-shadow] focus:border-brand focus:ring-2 focus:ring-brand/20"
          />
        </div>
        {error && (
          <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-unlock-submit"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-3.5 text-sm font-semibold text-paper transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-brand-deep disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LockKeyhole className="h-4 w-4" />}
          Sign in
        </button>
      </form>
    </div>
  );
};

const ManagerPanel = ({ user, onLogout }) => {
  const [info, setInfo] = useState(null);
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState("");
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  const loadInfo = () =>
    api
      .get("/installer/info")
      .then((r) => setInfo(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));

  useEffect(() => {
    loadInfo();
  }, []);

  const pickFile = (f) => {
    if (!f) return;
    const ok = [".msi", ".exe"].some((ext) => f.name.toLowerCase().endsWith(ext));
    if (!ok) {
      toast.error("Only .msi or .exe installer files are allowed.");
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) {
      toast.error("Choose an installer file first.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    if (version.trim()) fd.append("version", version.trim());
    setUploading(true);
    setProgress(0);
    try {
      const { data } = await api.post("/admin/installer", fd, {
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setInfo(data);
      setFile(null);
      setVersion("");
      if (inputRef.current) inputRef.current.value = "";
      toast.success("Installer uploaded — the download link is live.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setUploading(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(DOWNLOAD_URL);
      setCopied(true);
      toast.success("Direct download link copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy the link.");
    }
  };

  return (
    <div className="w-full max-w-2xl" data-testid="admin-panel">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <LogoMark className="h-10 w-10" />
          <div>
            <h1 className="font-display text-2xl font-medium tracking-tight">AssisDent Admin</h1>
            <p className="text-xs text-ink/50">Signed in as {user?.email}</p>
          </div>
        </div>
        <button
          onClick={onLogout}
          data-testid="admin-logout-button"
          className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 text-sm font-semibold text-ink transition-colors hover:border-brand hover:bg-mint-soft"
        >
          <LogOut className="h-4 w-4" />
          Log out
        </button>
      </div>

      <div className="mt-7 rounded-3xl border border-line bg-white p-6 shadow-soft sm:p-8">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-xl font-medium">Current installer</h2>
          <span className="rounded-full border border-line px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-ink/45">
            {info?.is_placeholder ? "demo placeholder" : "live"}
          </span>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <InfoTile label="File" value={info?.filename ?? "—"} testid="admin-filename" />
          <InfoTile label="Version" value={info?.version ? `v${info.version}` : "—"} testid="admin-version" />
          <InfoTile label="Size" value={info?.size_display ?? "—"} testid="admin-size" />
          <InfoTile label="Downloads" value={info?.downloads ?? 0} testid="admin-downloads-count" />
          <div className="sm:col-span-2">
            <InfoTile label="Last updated" value={info?.updated_at ? formatDate(info.updated_at) : "—"} testid="admin-updated-at" />
          </div>
        </div>
        {info?.is_placeholder && (
          <p
            className="mt-4 rounded-2xl border border-gold-border bg-gold-bg px-4 py-3 text-sm font-medium text-[#92400E]"
            data-testid="admin-placeholder-warning"
          >
            You&apos;re serving the demo placeholder. Upload your real installer below whenever
            it&apos;s ready.
          </p>
        )}
      </div>

      <div className="mt-5 rounded-3xl border border-line bg-white p-6 shadow-soft sm:p-8">
        <h2 className="font-display text-xl font-medium">Upload / replace installer</h2>
        <div
          data-testid="admin-upload-dropzone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            pickFile(e.dataTransfer.files?.[0]);
          }}
          className={`mt-5 cursor-pointer rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
            dragging ? "border-brand bg-mint-soft" : "border-line bg-paper hover:border-brand/60"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".msi,.exe"
            className="hidden"
            data-testid="admin-file-input"
            onChange={(e) => pickFile(e.target.files?.[0])}
          />
          <CloudUpload className={`mx-auto h-9 w-9 ${dragging ? "text-brand" : "text-ink/35"}`} />
          {file ? (
            <div className="mt-3 flex items-center justify-center gap-2 text-sm font-semibold text-ink">
              <FileArchive className="h-4 w-4 text-brand" />
              {file.name}
              <span className="font-mono text-xs font-normal text-ink/45">
                ({(file.size / (1024 * 1024)).toFixed(1)} MB)
              </span>
            </div>
          ) : (
            <>
              <p className="mt-3 text-sm font-semibold text-ink">
                Drop your .msi or .exe here, or click to browse
              </p>
              <p className="mt-1 text-xs text-ink/45">Up to 2 GB</p>
            </>
          )}
        </div>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="Version, e.g. 1.2.0 — leave blank to keep current"
            data-testid="admin-version-input"
            className="flex-1 rounded-full border border-line bg-paper px-4 py-3 text-sm font-medium outline-none transition-[border-color,box-shadow] focus:border-brand focus:ring-2 focus:ring-brand/20"
          />
          <button
            onClick={upload}
            disabled={uploading || !file}
            data-testid="admin-upload-button"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-7 py-3 text-sm font-semibold text-white transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-brand-deep disabled:cursor-not-allowed disabled:opacity-50"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CloudUpload className="h-4 w-4" />}
            {uploading ? `Uploading ${progress}%` : "Upload installer"}
          </button>
        </div>
        {uploading && (
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-line" data-testid="admin-upload-progress">
            <div className="h-full rounded-full bg-brand transition-[width] duration-300" style={{ width: `${progress}%` }} />
          </div>
        )}
        <div className="mt-5 flex items-center justify-between gap-2 rounded-full border border-line bg-paper px-4 py-2.5 font-mono text-xs text-ink/60">
          <span className="truncate" data-testid="admin-direct-link">
            {DOWNLOAD_URL}
          </span>
          <button
            onClick={copyLink}
            data-testid="admin-copy-direct-link"
            aria-label="Copy direct download link"
            className="shrink-0 rounded-full p-1.5 transition-colors hover:bg-mint-soft"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-brand" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        </div>
        <p className="mt-3 flex items-center gap-1.5 text-xs text-ink/40">
          <CalendarClock className="h-3.5 w-3.5" />
          Uploading replaces the current file everywhere — the direct link stays the same.
        </p>
      </div>
    </div>
  );
};

export default function Admin() {
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(null);
    toast.success("Signed out.");
  };

  return (
    <div className="min-h-screen bg-paper font-sans text-ink antialiased">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[420px] opacity-60"
        style={{ background: "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(204,251,241,0.8) 0%, rgba(250,253,251,0) 70%)" }}
      />
      <div className="relative z-10 flex min-h-screen items-center justify-center px-5 py-16 sm:px-8">
        {checking ? (
          <Loader2 className="h-7 w-7 animate-spin text-brand" data-testid="admin-checking-spinner" />
        ) : user ? (
          <ManagerPanel user={user} onLogout={logout} />
        ) : (
          <LoginCard onLoggedIn={setUser} />
        )}
      </div>
    </div>
  );
}
